import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentBranch } from '../../hooks/useCurrentBranch';
import wabaChatsActions from '../../store/wabaChatsStore/wabaChatsActions';
import WabaChats from './components/WabaChats';

export default function WabaChatsContainer() {
    const dispatch = useDispatch();
    const { currentBranch } = useCurrentBranch();
    const branchId = currentBranch?.id;

    const {
        conversations, conversationsStatus, conversationsTotal,
        activeConversationId, messages, messagesStatus, hasMoreMessages,
    } = useSelector(state => state.wabaChatsReducer);

    const mountedRef = useRef(true);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    useEffect(() => { return () => { mountedRef.current = false; }; }, []);

    // Disable page-level scroll on this page; make sidebar scroll independently
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const sidebar = document.body.querySelector('[class*="Sidebar_container"]');
        if (sidebar) {
            sidebar.style.height = '100vh';
            sidebar.style.overflowY = 'auto';
        }
        return () => {
            document.body.style.overflow = '';
            if (sidebar) {
                sidebar.style.height = '';
                sidebar.style.overflowY = '';
            }
        };
    }, []);

    useEffect(() => {
        if (branchId) dispatch(wabaChatsActions.getConversations(branchId));
        return () => dispatch(wabaChatsActions.clearAll());
    }, [branchId, dispatch]);

    useEffect(() => {
        if (branchId && activeConversationId) {
            dispatch(wabaChatsActions.getMessages(branchId, activeConversationId));
            dispatch(wabaChatsActions.markRead(branchId, activeConversationId));
        }
    }, [branchId, activeConversationId, dispatch]);

    const latestMessageTimestamp = useMemo(() => {
        if (messages.length === 0) return null;
        return messages[messages.length - 1].timestamp;
    }, [messages]);

    useEffect(() => {
        if (!branchId) return;
        const interval = setInterval(() => {
            if (!mountedRef.current) return;
            dispatch(wabaChatsActions.pollConversations(branchId));
            if (activeConversationId && latestMessageTimestamp) {
                dispatch(wabaChatsActions.pollNewMessages(branchId, activeConversationId, new Date(latestMessageTimestamp).getTime()));
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [branchId, activeConversationId, latestMessageTimestamp, dispatch]);

    const handleSelectConversation = useCallback((convId) => {
        dispatch(wabaChatsActions.setActiveConversation(convId));
    }, [dispatch]);

    const handleSendMessage = useCallback((text) => {
        if (branchId && activeConversationId && text.trim()) {
            dispatch(wabaChatsActions.sendTextMessage(branchId, activeConversationId, text));
            // Trigger immediate conversation list refresh after a short delay
            setTimeout(() => {
                dispatch(wabaChatsActions.pollConversations(branchId));
            }, 500);
        }
    }, [branchId, activeConversationId, dispatch]);

    const handleLoadMore = useCallback(() => {
        if (branchId && activeConversationId && messagesRef.current.length > 0) {
            const oldestTimestamp = new Date(messagesRef.current[0].timestamp).getTime();
            dispatch(wabaChatsActions.loadMoreMessages(branchId, activeConversationId, oldestTimestamp));
        }
    }, [branchId, activeConversationId, dispatch]);

    const handleSendMedia = useCallback((file) => {
        if (branchId && activeConversationId && file) {
            dispatch(wabaChatsActions.sendMediaMessage(branchId, activeConversationId, file));
            setTimeout(() => {
                dispatch(wabaChatsActions.pollConversations(branchId));
            }, 500);
        }
    }, [branchId, activeConversationId, dispatch]);

    const handleDismissMessage = useCallback((messageId) => {
        if (branchId && activeConversationId) {
            dispatch(wabaChatsActions.dismissMessage(branchId, activeConversationId, messageId));
        }
    }, [branchId, activeConversationId, dispatch]);

    return (
        <WabaChats
            conversations={conversations}
            conversationsStatus={conversationsStatus}
            activeConversationId={activeConversationId}
            messages={messages}
            messagesStatus={messagesStatus}
            hasMoreMessages={hasMoreMessages}
            onSelectConversation={handleSelectConversation}
            onSendMessage={handleSendMessage}
            onLoadMore={handleLoadMore}
            branchId={branchId}
            onDismiss={handleDismissMessage}
            onSendMedia={handleSendMedia}
        />
    );
}
